import { useEffect, useRef, useState } from "react";

import { REGION_LIST } from "@/constants/airport";
import { AIRPORT_MAP } from "@/constants/airport";
import { MEMBERSHIP_CONFIG, DEFAULT_PANORAMA_SEARCH_DAYS } from "@/constants/membership";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import useEvent from "@/hooks/useEvent";
import { LocationOption } from "@/pages/Routes/hooks/useLocationFilter";

import { LocationEnum } from "./useLocationFilter";

export interface ConditionType {
  fromLocation: LocationOption[];
  toLocation: LocationOption[];
}

export function parseSearchQuery(params: {
  fromLocation: string[];
  toLocation: string[];
}): ConditionType {
  return {
    fromLocation: params.fromLocation
      ? params.fromLocation.map((v) => {
        const [code, type] = v.split("_");

        if (code === REGION_LIST[0].code) {
          return { ...REGION_LIST[0], code, type };
        }

        return { code: code, type: type, name: "" };
      })
      : [],
    toLocation: params.toLocation
      ? params.toLocation.map((v) => {
        const [code, type] = v.split("_");

        if (code === REGION_LIST[0].code) {
          return { ...REGION_LIST[0], code, type };
        }

        return { code: code, type: type, name: "" };
      })
      : [],
  };
}

export function createSearchQuery(condition: ConditionType): any {
  return {
    departure: condition.fromLocation.map((ele) => `${ele.code}_${ele.type}`),
    arrival: condition.toLocation.map((ele) => `${ele.code}_${ele.type}`),
  };
}

const useCondition = (params: { fromLocation: string[]; toLocation: string[] }) => {
  const initParams = useRef(parseSearchQuery(params));
  const [condition, setCondition] = useState<ConditionType>(initParams.current);
  const store = useGlobalStoreContext();

  // First route page range
  const route_range = DEFAULT_PANORAMA_SEARCH_DAYS;
  useEffect(() => {
    if (!store.load) return;

    let data = initParams.current;

    if (!data) {
      let fromLocation = [{ ...REGION_LIST[0], type: LocationEnum.REGION }];

      if (store?.closest_airport) {
        const country = AIRPORT_MAP[store?.closest_airport]["country"];

        if (country) {
          fromLocation = [{ type: LocationEnum.COUNTRY, code: country, name: "United States" }];
        }
      }

      data = {
        fromLocation,
        toLocation: [{ ...REGION_LIST[0], type: LocationEnum.REGION }],
      };

      setCondition(data);
    }
  }, [route_range, store.load, store?.closest_airport]);

  const onLocationSwapChange = useEvent(() => {
    setCondition((pre) => {
      return {
        ...pre,
        fromLocation: pre.toLocation,
        toLocation: pre.fromLocation,
      };
    });
  });

  return {
    condition,
    setCondition,
    onLocationSwapChange,
  };
};

export default useCondition;
